import React, { useEffect, useState, useCallback } from "react";
import { Users, UserCheck, Clock, UserX, BarChart3, Wallet, Building2, CalendarOff, Plane, Timer } from "lucide-react";
import { Card, Spinner, PageHeader } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, BranchFilter } from "../../components/finance";

function StatCard({ icon: Icon, tone, label, value, testid }) {
  const tones = { primary: "bg-primary-light text-primary", success: "bg-success-light text-success-text", warn: "bg-warn-light text-warn-text", danger: "bg-danger-light text-danger-text", info: "bg-blue-100 text-blue-700" };
  return (
    <Card className="flex items-center gap-3" data-testid={testid}>
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={20} /></div>
      <div>
        <p className="text-xs text-ink-mute">{label}</p>
        <p className="font-heading text-xl font-bold text-ink">{value}</p>
      </div>
    </Card>
  );
}

export default function HRDashboard() {
  const toast = useToast();
  const { user } = useAuth();
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const [branchId, setBranchId] = useState("");
  const [d, setD] = useState(null);
  const [org, setOrg] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const canSeeExtra = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, o, a] = await Promise.all([
        api.get("/hr/dashboard", { params: { branch_id: branchId || undefined } }),
        api.get("/hr/org-structure"),
        api.get("/hr/analytics/attendance", { params: { branch_id: branchId || undefined } }),
      ]);
      setD(dash.data); setOrg(o.data); setAnalytics(a.data);
    } catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [branchId, toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="hr-dashboard-page">
      <PageHeader title="Dashboard HRD" subtitle="Karyawan, kehadiran, KPI & estimasi payroll" icon={Users}>
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-branch-filter" />}
      </PageHeader>

      {loading || !d ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard icon={Users} tone="primary" label="Total Karyawan" value={d.total_employees} testid="hr-stat-total" />
            <StatCard icon={UserCheck} tone="success" label="Hadir Hari Ini" value={d.present_today} testid="hr-stat-present" />
            <StatCard icon={Clock} tone="warn" label="Terlambat Hari Ini" value={d.late_today} testid="hr-stat-late" />
            <StatCard icon={UserX} tone="danger" label="Tidak Hadir" value={d.absent_today} testid="hr-stat-absent" />
            <StatCard icon={BarChart3} tone="info" label="Rata-rata KPI" value={d.kpi_average} testid="hr-stat-kpi" />
            <StatCard icon={Wallet} tone="success" label="Estimasi Payroll" value={rp(d.payroll_estimate)} testid="hr-stat-payroll" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card data-testid="hr-by-status">
              <h3 className="mb-3 font-heading font-bold text-ink">Status Kepegawaian</h3>
              <div className="space-y-2">
                {Object.entries(d.by_status).map(([s, c]) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="text-ink-soft">{s}</span>
                    <span className="font-semibold text-ink">{c}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Org structure 9D */}
            <Card data-testid="hr-org-structure">
              <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Building2 size={18} /> Struktur Organisasi</h3>
              <p className="mb-2 text-sm font-semibold text-primary">{org?.company}</p>
              <div className="space-y-3">
                {org?.branches.map((b) => (
                  <div key={b.branch_id} className="border-l-2 border-line pl-3">
                    <p className="text-sm font-semibold text-ink">{b.branch_name} <span className="text-xs font-normal text-ink-mute">({b.employee_count})</span></p>
                    {b.departments.map((dep) => (
                      <div key={dep.name} className="ml-2 mt-1">
                        <p className="text-xs font-medium text-ink-soft">{dep.name}</p>
                        <p className="ml-2 text-xs text-ink-mute">{dep.employees.map((e) => e.name).join(", ")}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Management attendance analytics (period to date) */}
          {analytics && (
            <div className="space-y-4" data-testid="hr-analytics">
              <h3 className="font-heading text-lg font-bold text-ink">Analitik Kehadiran · {analytics.period}</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={Clock} tone="warn" label="Total Terlambat" value={analytics.totals.late_count} testid="hr-an-late" />
                <StatCard icon={UserX} tone="danger" label="Pulang Awal" value={analytics.totals.early_out_count} testid="hr-an-earlyout" />
                <StatCard icon={CalendarOff} tone="info" label="Day Off Terpakai" value={analytics.totals.day_off_used} testid="hr-an-dayoff" />
                <StatCard icon={Plane} tone="primary" label="Cuti Disetujui" value={analytics.leave_counts.approved} testid="hr-an-leave-approved" />
                {canSeeExtra && analytics.totals.total_extra_hours != null && (
                  <StatCard icon={Timer} tone="success" label="Extra Hours (jam)" value={analytics.totals.total_extra_hours} testid="hr-an-extra" />
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card data-testid="hr-an-by-branch">
                  <h4 className="mb-3 font-heading font-bold text-ink">Kehadiran per Cabang</h4>
                  {analytics.by_branch.length === 0 ? <p className="text-sm text-ink-mute">Belum ada data</p> : (
                    <table className="w-full text-sm">
                      <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                        <tr><th className="py-1.5">Cabang</th><th className="py-1.5 text-right">Hadir</th><th className="py-1.5 text-right">Telat</th><th className="py-1.5 text-right">Pulang Awal</th></tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {analytics.by_branch.map((b) => (
                          <tr key={b.branch_name}>
                            <td className="py-1.5 font-medium text-ink">{b.branch_name}</td>
                            <td className="py-1.5 text-right text-ink-soft">{b.present}</td>
                            <td className="py-1.5 text-right text-warn-text">{b.late}</td>
                            <td className="py-1.5 text-right text-danger-text">{b.early_out}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card>

                {canSeeExtra && (
                  <Card data-testid="hr-an-top-extra">
                    <h4 className="mb-1 flex items-center gap-2 font-heading font-bold text-ink"><Timer size={16} /> Extra Hours Teratas</h4>
                    <p className="mb-3 text-xs text-ink-mute">Datang lebih awal / pulang lebih lambat — untuk evaluasi & bonus (bukan lembur dibayar).</p>
                    {analytics.top_extra_hours.length === 0 ? <p className="text-sm text-ink-mute">Belum ada extra hours</p> : (
                      <ul className="divide-y divide-line">
                        {analytics.top_extra_hours.map((e) => (
                          <li key={e.employee_id} className="flex items-center justify-between py-2 text-sm">
                            <span className="text-ink">{e.employee_name} <span className="text-xs text-ink-mute">· {e.branch_name}</span></span>
                            <span className="font-semibold text-success-text">{e.extra_hours} jam</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
